/**
 * Logger utility for the frontend
 * 
 * This utility provides logging functions that respect the current environment.
 * In production, console logs are suppressed unless explicitly enabled.
 */

/**
 * Logger interface
 */
export interface Logger {
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, ...args: any[]) => void;
}

/**
 * Production logger that suppresses debug and info logs
 */
const productionLogger: Logger = {
  debug: () => {}, // No-op in production
  info: () => {},  // No-op in production
  warn: (message: string, ...args: any[]) => {
    console.warn(message, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(message, ...args);
  }
};

/**
 * Development logger that outputs all logs
 */
const developmentLogger: Logger = {
  debug: (message: string, ...args: any[]) => {
    console.debug(message, ...args);
  },
  info: (message: string, ...args: any[]) => {
    console.info(message, ...args);
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(message, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(message, ...args);
  }
};

// Determine which logger to use based on environment
// This function is called every time to ensure it uses the current environment setting
const getLogger = (): Logger => {
  const isProduction = process.env.NODE_ENV === 'production';
  return isProduction ? productionLogger : developmentLogger;
};

// Create a proxy logger that delegates to the appropriate logger based on the current environment
const logger: Logger = {
  debug: (message: string, ...args: any[]) => getLogger().debug(message, ...args),
  info: (message: string, ...args: any[]) => getLogger().info(message, ...args),
  warn: (message: string, ...args: any[]) => getLogger().warn(message, ...args),
  error: (message: string, ...args: any[]) => getLogger().error(message, ...args)
};

export default logger;
